import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/MainLayout';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../../components/ui/tooltip';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../../components/ui/sheet';
import { toast } from 'sonner';
import { isSuperAdmin, getCurrentAccount } from '../../lib/auth';
import {
  TrendingDown, TrendingUp, RefreshCw, FileText,
  Loader2, Wallet, Calendar, ExternalLink, Eye, Banknote, Receipt,
  AlertTriangle, ArrowDownToLine, ArrowUpFromLine, Info, Download,
} from 'lucide-react';

const fmtC = (v) => v == null ? '\u2014' : '$' + Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtD = (d) => d ? new Date(d).toLocaleDateString() : '\u2014';

// Type → badge map.
const TYPE_BADGE = {
  // Bills view
  bill_payment: { label: 'Bill Payment', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  service_charge: { label: 'Service Fee', cls: 'bg-violet-50 text-violet-700 border-violet-200' },
  refund: { label: 'Refund', cls: 'bg-orange-50 text-orange-700 border-orange-200' },
  bank_credit: { label: 'Bank Credit', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  // Bank view (by source_tag)
  billportal_ach: { label: 'Fund Pull (BP ACH)', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  property_ach: { label: 'Fund Pull (Property)', cls: 'bg-teal-50 text-teal-700 border-teal-200' },
  vendor_refund: { label: 'Vendor Refund', cls: 'bg-cyan-50 text-cyan-700 border-cyan-200' },
  other_credit: { label: 'Other Credit', cls: 'bg-slate-100 text-slate-700 border-slate-200' },
  vendor_payment: { label: 'Vendor Payment', cls: 'bg-red-50 text-red-700 border-red-200' },
  pre_funding: { label: 'Pre-funding', cls: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  refund_to_payer: { label: 'Refunded', cls: 'bg-orange-50 text-orange-700 border-orange-200' },
  anchor_refund_payout: { label: 'Refunded', cls: 'bg-orange-50 text-orange-700 border-orange-200' },
  internal_transfer: { label: 'Internal Transfer', cls: 'bg-sky-50 text-sky-700 border-sky-200' },
  service_fee: { label: 'Service Fee', cls: 'bg-violet-50 text-violet-700 border-violet-200' },
  r03_reversal: { label: 'R03 Return', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  nsf_fee: { label: 'NSF Charge', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  bank_fee: { label: 'Bank Fee', cls: 'bg-slate-50 text-slate-700 border-slate-200' },
};

const VIEW_TABS = [
  { key: 'bills', label: 'Bill Transactions', icon: Receipt,
    desc: 'Credits from bank statements vs Debits derived from bills (payments + service fees + completed refunds).' },
  { key: 'bank', label: 'Bank Transactions', icon: Banknote,
    desc: 'Raw bank statement lines — every credit and debit as the bank reported, including R03 returns and bank fees.' },
];

const BANK_FILTERS = [
  { key: 'all', label: 'All lines' },
  { key: 'fund_pulls', label: 'Funds Pulled (credits)' },
  { key: 'fund_deposits', label: 'Fund Deposits / Refunds (credits)' },
  { key: 'vendor_payments', label: 'Vendor Payments (debits)' },
  { key: 'pre_funding', label: 'Pre-funding (debits)' },
  { key: 'refund', label: 'Refunds (debits)' },
  { key: 'returns_fees', label: 'Returns & Fees (debits)' },
];

const BILLS_TYPES = new Set(['bill_payment', 'service_charge', 'refund']);

function downloadCSV(filename, rows) {
  if (!rows || !rows.length) {
    toast.error('Nothing to download');
    return;
  }
  const headers = Object.keys(rows[0]);
  const escape = (v) => {
    if (v == null) return '';
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [
    headers.join(','),
    ...rows.map((r) => headers.map((h) => escape(r[h])).join(',')),
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

export default function CreditDebitReportPage() {
  const navigate = useNavigate();
  const currentAccount = getCurrentAccount();
  const isSuper = isSuperAdmin();

  const [view, setView] = useState('bills');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [bankFilter, setBankFilter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [entryType, setEntryType] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billDrawerOpen, setBillDrawerOpen] = useState(false);
  const [billDrawerData, setBillDrawerData] = useState(null);

  const accountId = currentAccount?.id;

  const fetchData = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const params = { account_id: accountId, limit: 5000, view };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (entryType && entryType !== 'all') params.entry_type = entryType;
      if (view === 'bank') {
        if (vendorFilter && vendorFilter !== 'all') params.vendor = vendorFilter;
        if (bankFilter && bankFilter !== 'all') params.bank_filter = bankFilter;
      }
      const { data: d } = await api.get('/bank-statements/credit-debit-report', { params });
      setData(d);
    } catch (e) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [accountId, dateFrom, dateTo, view, vendorFilter, bankFilter, entryType]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // When user switches tabs, reset bank-specific filters back to defaults
  useEffect(() => {
    if (view !== 'bank') {
      setVendorFilter('all');
      setBankFilter('all');
    }
  }, [view]);

  const stats = data?.stats || {};
  const lines = data?.lines || [];
  const totalRows = data?.total || 0;
  const vendors = data?.vendors || [];

  const openBillDrawer = (line) => {
    setBillDrawerData(line);
    setBillDrawerOpen(true);
  };
  const openBillFullPage = (bill_id) => {
    if (!bill_id) return;
    window.open(`/bills/${bill_id}`, '_blank', 'noopener,noreferrer');
  };

  const handleDownload = () => {
    if (!lines.length) return toast.error('Nothing to download');
    const tabName = view === 'bills' ? 'Bill_Transactions' : 'Bank_Transactions';
    const orgSlug = (currentAccount?.organisation_name || 'org').replace(/\s+/g, '_');
    const date = new Date().toISOString().slice(0, 10);
    let rows;
    if (view === 'bills') {
      rows = lines.map((l) => ({
        Post_Date: l.post_date,
        Type: TYPE_BADGE[l.type]?.label || l.type,
        Line_Item: l.line_item,
        Credit: l.credit > 0 ? l.credit : '',
        Debit: l.debit > 0 ? l.debit : '',
        Running_Balance: l.balance,
        Invoice_Number: l.meta?.invoice_number || '',
        Account_Number: l.meta?.account_number || '',
        Supplier: l.meta?.supplier_name || '',
      }));
    } else {
      rows = lines.map((l) => ({
        Post_Date: l.post_date,
        Type: TYPE_BADGE[l.type]?.label || l.type,
        Matched_Vendor: l.matched_vendor || '',
        Description: l.line_item,
        Credit: l.credit > 0 ? l.credit : '',
        Debit: l.debit > 0 ? l.debit : '',
        Running_Balance: l.balance,
        Bank_Account: l.meta?.bank_account_no || '',
        Is_Fund_Pull: l.is_fund_pull ? 'Y' : 'N',
        Source_Tag: l.type,
      }));
    }
    downloadCSV(`${orgSlug}_${tabName}_${date}.csv`, rows);
  };

  const activeTab = VIEW_TABS.find(t => t.key === view) || VIEW_TABS[0];

  const bankStats = useMemo(() => {
    if (view !== 'bank') return [];
    return [
      { label: 'Net Fund Pulls', val: stats.net_fund_pulls, color: 'text-emerald-700', bg: 'bg-emerald-50/40',
        sub: `Gross ${fmtC(stats.gross_fund_pulls)} − R03 ${fmtC(stats.r03_reversals)}`, icon: ArrowDownToLine, iconCls: 'text-emerald-600',
        totalLabel: 'Net Fund Pulls',
        breakdown: [
          { label: 'Total credits received', val: stats.total_credits, sign: '+' },
          { label: 'Internal transfer credits', val: -(stats.internal_transfer_credits || 0), sign: '−' },
          { label: 'R03 returns (bounced pulls)', val: -(stats.r03_returns || 0), sign: '−' },
          ...(Math.abs(stats.other_non_pull_credits || 0) > 0.01
            ? [{ label: 'Other non-pull credits', val: -(stats.other_non_pull_credits || 0), sign: '−' }] : []),
          ...((stats.r03_reversals || 0) > 0.01
            ? [{ label: 'R03 reversals (reversed pulls)', val: -(stats.r03_reversals || 0), sign: '−' }] : []),
        ] },
      { label: 'Total Debits', val: stats.total_debits, color: 'text-red-700', bg: 'bg-red-50/40',
        sub: `Vendor pmts ${fmtC(stats.vendor_payments)} + Refunds ${fmtC(stats.refund_debits)} + more`,
        icon: ArrowUpFromLine, iconCls: 'text-red-600',
        totalLabel: 'Total Debits',
        breakdown: [
          { label: 'Vendor payments', val: stats.vendor_payments, sign: '+' },
          { label: 'Refunds (to payer / Anchor)', val: stats.refund_debits, sign: '+' },
          { label: 'Pre-funding', val: stats.pre_funding, sign: '+' },
          { label: 'Internal transfers', val: stats.internal_transfers, sign: '+' },
          { label: 'Service fees', val: stats.service_fees, sign: '+' },
          { label: 'NSF + bank fees', val: (stats.nsf_fees || 0) + (stats.bank_fees || 0), sign: '+' },
        ] },
      { label: 'R03 Returns', val: stats.r03_reversals, color: 'text-amber-700', bg: 'bg-amber-50/40',
        sub: 'Failed pulls reversed by bank', icon: AlertTriangle, iconCls: 'text-amber-600' },
      { label: 'NSF + Bank Fees', val: (stats.nsf_fees || 0) + (stats.bank_fees || 0), color: 'text-slate-700', bg: 'bg-slate-50/40',
        sub: `NSF ${fmtC(stats.nsf_fees)} + Bank ${fmtC(stats.bank_fees)}`, icon: Banknote, iconCls: 'text-slate-600' },
    ];
  }, [view, stats]);

  return (
    <MainLayout>
      <TooltipProvider delayDuration={150}>
      <div className="space-y-4" data-testid="credit-debit-report-page">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Credit / Debit Report</h2>
          <p className="text-sm text-muted-foreground">{activeTab.desc}</p>
        </div>

        {/* Tabs */}
        <div className="flex items-end gap-1 border-b border-border" data-testid="cd-view-tabs">
          {VIEW_TABS.map((t) => {
            const Icon = t.icon;
            const isActive = view === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setView(t.key)}
                data-testid={`cd-tab-${t.key}`}
                className={`px-4 py-2 text-xs font-semibold border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${
                  isActive
                    ? 'border-blue-600 text-blue-700'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/40'
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Stats — different per tab */}
        {view === 'bills' ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="cd-stats-bills">
            <div className="border rounded-lg p-4 bg-card">
              <div className="flex items-center gap-2 mb-1">
                <Wallet className="h-3.5 w-3.5 text-blue-600" />
                <span className="text-[10px] font-semibold uppercase tracking-wider text-blue-600">Current Balance</span>
              </div>
              <p className={`text-xl font-bold tabular-nums ${stats.current_balance < 0 ? 'text-red-700' : ''}`} data-testid="stat-current">{fmtC(stats.current_balance)}</p>
              <p className="text-[10px] text-muted-foreground mt-1">Credits − Debits (settled)</p>
            </div>
            <div className="border rounded-lg p-4 bg-card">
              <div className="flex items-center gap-2 mb-1">
                <Wallet className="h-3.5 w-3.5 text-violet-600" />
                <span className="text-[10px] font-semibold uppercase tracking-wider text-violet-600">Available Balance</span>
              </div>
              <p className={`text-xl font-bold tabular-nums ${stats.available_balance < 0 ? 'text-red-700' : ''}`} data-testid="stat-available">{fmtC(stats.available_balance)}</p>
              <p className="text-[10px] text-muted-foreground mt-1">Current − {fmtC(stats.pending_refund || 0)} pending refunds</p>
            </div>
            <div className="border rounded-lg p-4 bg-emerald-50/40">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="h-3.5 w-3.5 text-emerald-600" />
                <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-600">Total Credits (YTD)</span>
              </div>
              <p className="text-xl font-bold text-emerald-700 tabular-nums" data-testid="stat-ytd-credits">{fmtC(stats.ytd_credits)}</p>
            </div>
            <div className="border rounded-lg p-4 bg-red-50/40">
              <div className="flex items-center gap-2 mb-1">
                <TrendingDown className="h-3.5 w-3.5 text-red-600" />
                <span className="text-[10px] font-semibold uppercase tracking-wider text-red-600">Total Debits (YTD)</span>
              </div>
              <p className="text-xl font-bold text-red-700 tabular-nums" data-testid="stat-ytd-debits">{fmtC(stats.ytd_debits)}</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="cd-stats-bank">
            {bankStats.map((s, i) => {
              const Icon = s.icon;
              const tile = (
                <div key={i} className={`border rounded-lg p-4 ${s.bg} ${s.breakdown ? 'cursor-help' : ''}`} data-testid={`cd-bank-stat-${i}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className={`h-3.5 w-3.5 ${s.iconCls}`} />
                    <span className={`text-[10px] font-semibold uppercase tracking-wider ${s.iconCls}`}>{s.label}</span>
                  </div>
                  <p className={`text-xl font-bold tabular-nums ${s.color}`}>{fmtC(s.val)}</p>
                  <p className="text-[10px] text-muted-foreground mt-1 truncate">{s.sub}</p>
                </div>
              );
              if (!s.breakdown) return tile;
              return (
                <Tooltip key={i}>
                  <TooltipTrigger asChild>{tile}</TooltipTrigger>
                  <TooltipContent side="top" className="text-[11px] p-3 max-w-[300px]" data-testid={`cd-bank-stat-${i}-tip`}>
                    <p className="font-semibold mb-1.5">{s.label} breakdown</p>
                    <div className="space-y-0.5">
                      {s.breakdown.map((b, bi) => (
                        <div key={bi} className="flex items-center justify-between gap-6">
                          <span className="text-muted-foreground">
                            {b.sign && <span className={`mr-1 font-semibold ${b.sign === '−' ? 'text-amber-600' : 'text-emerald-600'}`}>{b.sign}</span>}
                            {b.label}
                          </span>
                          <span className="tabular-nums font-medium">{fmtC(b.val)}</span>
                        </div>
                      ))}
                      <div className="flex items-center justify-between gap-6 border-t border-border mt-1 pt-1">
                        <span className="font-semibold">{s.totalLabel || s.label}</span>
                        <span className="tabular-nums font-bold">{fmtC(s.val)}</span>
                      </div>
                    </div>
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        )}

        {/* Filters + Download */}
        <div className="border rounded-lg p-3 bg-card flex items-center gap-3 flex-wrap" data-testid="cd-filters">
          <div className="flex items-center gap-2">
            <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 text-xs w-[140px]" data-testid="filter-date-from" />
            <span className="text-muted-foreground text-xs">to</span>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 text-xs w-[140px]" data-testid="filter-date-to" />
          </div>
          {/* Entry-type filter (credit / debit / refund) — works in both views. */}
          <Select value={entryType} onValueChange={setEntryType}>
            <SelectTrigger className="h-8 text-xs w-[150px]" data-testid="filter-entry-type">
              <SelectValue placeholder="All entries" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">All entries</SelectItem>
              <SelectItem value="credit" className="text-xs">Credits only</SelectItem>
              <SelectItem value="debit" className="text-xs">Debits only</SelectItem>
              <SelectItem value="refund" className="text-xs">Refunds only</SelectItem>
            </SelectContent>
          </Select>
          {view === 'bank' && (
            <>
              <Select value={bankFilter} onValueChange={setBankFilter}>
                <SelectTrigger className="h-8 text-xs w-[200px]" data-testid="filter-bank-type">
                  <SelectValue placeholder="All lines" />
                </SelectTrigger>
                <SelectContent>
                  {BANK_FILTERS.map((f) => (
                    <SelectItem key={f.key} value={f.key} className="text-xs">{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={vendorFilter} onValueChange={setVendorFilter}>
                <SelectTrigger className="h-8 text-xs w-[220px]" data-testid="filter-bank-vendor">
                  <SelectValue placeholder="All vendors" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" className="text-xs">All vendors ({vendors.length})</SelectItem>
                  {vendors.map((v) => (
                    <SelectItem key={v.name} value={v.name} className="text-xs">
                      {v.name} ({v.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          )}
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} className="h-8 text-xs" data-testid="cd-refresh-btn">
            {loading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownload} disabled={loading || !lines.length} className="h-8 text-xs" data-testid="cd-download-btn">
            <Download className="h-3 w-3 mr-1" /> Download CSV
          </Button>
          <span className="ml-auto text-[11px] text-muted-foreground tabular-nums">
            {loading ? '...' : `${totalRows.toLocaleString()} entries · Net ${fmtC(stats.net)}`}
          </span>
        </div>

        {/* Gap note — only on Bill Transactions tab */}
        {view === 'bills' && !loading && (
          <div className="text-[11px] text-amber-700 bg-amber-50/60 border border-amber-200 rounded-md px-3 py-2 flex items-start gap-2" data-testid="cd-gap-note">
            <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>
              Some bank transactions may still be pending upload, so a few credits or debits here could be missing.
              Switch to <span className="font-semibold">Bank Transactions</span> to see exactly what your bank reported.
            </span>
          </div>
        )}

        {/* Ledger table */}
        <div className="border rounded-lg bg-card">
          <table className="w-full text-xs table-fixed" data-testid="cd-ledger-table">
            <colgroup>
              <col className="w-[92px]" />
              <col />
              <col className="w-[120px]" />
              <col className="w-[120px]" />
              <col className="w-[120px]" />
              <col className="w-[64px]" />
            </colgroup>
            <thead className="bg-muted/30 border-b">
              <tr>
                <th className="text-left px-2 py-2 font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Date</th>
                <th className="text-left px-2 py-2 font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Line Item</th>
                <th className="text-right px-2 py-2 font-semibold uppercase tracking-wider text-[10px] text-emerald-600">Credits</th>
                <th className="text-right px-2 py-2 font-semibold uppercase tracking-wider text-[10px] text-red-600">Debits</th>
                <th className="text-right px-2 py-2 font-semibold uppercase tracking-wider text-[10px] text-blue-600">Balance</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i}><td colSpan={6} className="px-3 py-2"><Skeleton className="h-4 w-full" /></td></tr>
                ))
              ) : lines.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-muted-foreground">
                    <FileText className="h-10 w-10 mx-auto mb-2 opacity-30" />
                    <p>No ledger entries for the selected filters</p>
                    <p className="text-[11px] mt-2">Try uploading a bank statement under <span className="underline">Payments &gt; Upload Credit File</span></p>
                  </td>
                </tr>
              ) : (
                lines.map((l, i) => {
                  const badge = TYPE_BADGE[l.type] || { label: l.type || 'Line', cls: 'bg-muted text-muted-foreground border-muted' };
                  const isBillLine = BILLS_TYPES.has(l.type);
                  const canOpenDrawer = view === 'bills' && isBillLine;
                  return (
                    <Tooltip key={i}>
                      <TooltipTrigger asChild>
                        <tr
                          className="border-b border-border/40 hover:bg-muted/30 cursor-help"
                          data-testid={`cd-row-${i}`}
                        >
                          <td className="px-2 py-2 text-muted-foreground tabular-nums truncate">{fmtD(l.post_date)}</td>
                          <td className="px-2 py-2 min-w-0">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <span className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded border text-[9px] font-semibold uppercase tracking-wider ${badge.cls}`}>
                                {badge.label}
                              </span>
                              {view === 'bank' && l.matched_vendor && (
                                <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 text-[9px] font-semibold">
                                  {l.matched_vendor}
                                </span>
                              )}
                              <span className="truncate min-w-0 flex-1">{l.line_item}</span>
                            </div>
                          </td>
                          <td className="px-2 py-2 text-right tabular-nums text-emerald-700 font-semibold">
                            {l.credit > 0 ? fmtC(l.credit) : ''}
                          </td>
                          <td className="px-2 py-2 text-right tabular-nums text-red-700 font-semibold">
                            {l.debit > 0 ? fmtC(l.debit) : ''}
                          </td>
                          <td className={`px-2 py-2 text-right tabular-nums font-mono text-[11px] ${l.balance < 0 ? 'text-red-700' : 'text-blue-700'}`} data-testid={`cd-balance-${i}`}>
                            {fmtC(l.balance)}
                          </td>
                          <td className="px-2 py-2 text-right">
                            {canOpenDrawer && (
                              <button
                                onClick={() => openBillDrawer(l)}
                                className="text-[11px] text-blue-600 hover:underline inline-flex items-center gap-1"
                                data-testid={`cd-open-bill-${i}`}
                              >
                                <Eye className="h-3 w-3" />
                              </button>
                            )}
                          </td>
                        </tr>
                      </TooltipTrigger>
                      <TooltipContent side="top" align="start" sideOffset={6} collisionPadding={20} avoidCollisions className="text-xs p-3 max-w-[380px]">
                        <p className="font-semibold mb-1.5">{badge.label}</p>
                        <p className="text-[11px] mb-2 break-words">{l.line_item}</p>
                        {view === 'bills' && l.meta && isBillLine && (
                          <div className="space-y-0.5 text-[11px]">
                            <div className="flex justify-between gap-3"><span className="text-muted-foreground">Account #</span><span className="font-mono">{l.meta.account_number || '—'}</span></div>
                            <div className="flex justify-between gap-3"><span className="text-muted-foreground">Invoice #</span><span className="font-mono">{l.meta.invoice_number || '—'}</span></div>
                            <div className="flex justify-between gap-3"><span className="text-muted-foreground">Issue Date</span><span>{fmtD(l.meta.invoice_date)}</span></div>
                            <div className="flex justify-between gap-3"><span className="text-muted-foreground">Due Date</span><span>{fmtD(l.meta.due_date)}</span></div>
                            <div className="flex justify-between gap-3"><span className="text-muted-foreground">Total Due</span><span className="font-mono">{fmtC(l.meta.total_amount_due)}</span></div>
                            <div className="flex justify-between gap-3"><span className="text-muted-foreground">Approved Amt</span><span className="font-mono">{fmtC(l.meta.payment_amount)}</span></div>
                          </div>
                        )}
                        {view === 'bank' && l.meta && (
                          <div className="space-y-0.5 text-[11px]">
                            {l.matched_vendor && (
                              <div className="flex justify-between gap-3"><span className="text-muted-foreground">Matched Vendor</span><span className="font-semibold">{l.matched_vendor}</span></div>
                            )}
                            <div className="flex justify-between gap-3"><span className="text-muted-foreground">Bank Acct</span><span className="font-mono">{l.meta.bank_account_no || '—'}</span></div>
                            <div className="flex justify-between gap-3"><span className="text-muted-foreground">Check #</span><span className="font-mono">{l.meta.check_no || '—'}</span></div>
                            <div className="flex justify-between gap-3"><span className="text-muted-foreground">Source Tag</span><span className="font-mono">{l.meta.source_tag || '—'}</span></div>
                            <div className="flex justify-between gap-3"><span className="text-muted-foreground">Partner</span><span>{l.meta.partner || '—'}</span></div>
                          </div>
                        )}
                      </TooltipContent>
                    </Tooltip>
                  );
                })
              )}
            </tbody>
            {/* Totals row — semi-transparent backdrop + per-cell tooltips */}
            {!loading && lines.length > 0 && (
              <tfoot className="bg-muted/60 backdrop-blur-sm sticky bottom-0">
                <tr className="border-t-2 border-border font-semibold" data-testid="cd-totals-row">
                  <td colSpan={2} className="px-2 py-2 text-[11px] uppercase tracking-wider text-muted-foreground">
                    Totals (filtered)
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums text-emerald-700">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="cursor-help">
                          <div className="underline decoration-dotted underline-offset-2">{fmtC(stats.total_credits)}</div>
                          {stats.credit_count != null && (
                            <div className="text-[10px] font-normal text-emerald-600/80" data-testid="cd-credit-count">{stats.credit_count.toLocaleString()} {stats.credit_count === 1 ? 'row' : 'rows'}</div>
                          )}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-[11px] p-2 max-w-[280px]">
                        <p className="font-semibold mb-1">Total Credits (filtered)</p>
                        <p className="text-muted-foreground">
                          {view === 'bills'
                            ? 'Sum of every credit row visible above — uploaded bank-statement deposits within the date range.'
                            : 'Sum of every credit row visible above — fund pulls, vendor refunds, and other deposits straight from the bank statement.'}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums text-red-700">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="cursor-help">
                          <div className="underline decoration-dotted underline-offset-2">{fmtC(stats.total_debits)}</div>
                          {stats.debit_count != null && (
                            <div className="text-[10px] font-normal text-red-600/80" data-testid="cd-debit-count">
                              {stats.debit_count.toLocaleString()} {view === 'bills' ? (stats.debit_count === 1 ? 'bill' : 'bills') : (stats.debit_count === 1 ? 'row' : 'rows')}
                            </div>
                          )}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-[11px] p-2 max-w-[280px]">
                        <p className="font-semibold mb-1">Total Debits (filtered)</p>
                        <p className="text-muted-foreground">
                          {view === 'bills'
                            ? 'Sum of every debit row above — derived from paid bills (paid_amount + service charges + completed refunds) for BillPortal-paid bills only.'
                            : 'Sum of every debit row above — vendor payments out, R03 reversals, NSF charges, and bank fees as they appear on the bank statement.'}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </td>
                  <td className={`px-2 py-2 text-right tabular-nums font-mono ${stats.current_balance < 0 ? 'text-red-700' : 'text-blue-700'}`}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="underline decoration-dotted underline-offset-2 cursor-help">{fmtC(stats.current_balance)}</span>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-[11px] p-2 max-w-[280px]">
                        <p className="font-semibold mb-1">Running Balance</p>
                        <p className="text-muted-foreground">
                          Final running balance after applying every visible row chronologically:
                          starts at 0, +credit / −debit per line.
                          Equals Total Credits − Total Debits.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* Source tag breakdown — only bank view */}
        {view === 'bank' && !loading && stats.by_source_tag && (
          <div className="border rounded-lg bg-card p-4" data-testid="cd-bank-breakdown">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">By Bank Source Tag</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {Object.entries(stats.by_source_tag).sort((a, b) =>
                (b[1].credits + b[1].debits) - (a[1].credits + a[1].debits)
              ).map(([tag, v]) => {
                const badge = TYPE_BADGE[tag] || { label: tag, cls: 'bg-muted text-muted-foreground border-muted' };
                return (
                  <div key={tag} className="border rounded p-2">
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[9px] font-semibold uppercase tracking-wider ${badge.cls}`}>
                      {badge.label}
                    </span>
                    <p className="text-[11px] mt-1 tabular-nums">
                      {v.credits > 0 && <span className="text-emerald-700">+{fmtC(v.credits)} </span>}
                      {v.debits > 0 && <span className="text-red-700">−{fmtC(v.debits)}</span>}
                    </p>
                    <p className="text-[10px] text-muted-foreground">{v.count.toLocaleString()} lines</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Bill Drawer */}
      <Sheet open={billDrawerOpen} onOpenChange={setBillDrawerOpen}>
        <SheetContent side="right" className="w-[480px] p-0 flex flex-col" data-testid="cd-bill-drawer">
          <SheetHeader className="px-6 py-4 border-b border-border">
            <SheetTitle className="text-sm">Bill Details</SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {billDrawerData?.meta ? (
              <div className="space-y-4">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Line Item</p>
                  <p className="text-sm break-words">{billDrawerData.line_item}</p>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-xs">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">Account #</p>
                    <p className="font-mono">{billDrawerData.meta.account_number || '—'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">Invoice #</p>
                    <p className="font-mono">{billDrawerData.meta.invoice_number || '—'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">Issue Date</p>
                    <p>{fmtD(billDrawerData.meta.invoice_date)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">Due Date</p>
                    <p>{fmtD(billDrawerData.meta.due_date)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">Total Amount Due</p>
                    <p className="font-mono font-semibold">{fmtC(billDrawerData.meta.total_amount_due)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">New Charges</p>
                    <p className="font-mono">{fmtC(billDrawerData.meta.current_bill_amount)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">Approved Amount</p>
                    <p className="font-mono">{fmtC(billDrawerData.meta.payment_amount)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">Service Address</p>
                    <p className="text-[11px]">{billDrawerData.meta.service_address || '—'}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-xs pt-3 border-t">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-600 mb-0.5">Credit on this Line</p>
                    <p className="font-mono font-bold text-emerald-700">{billDrawerData.credit > 0 ? fmtC(billDrawerData.credit) : '—'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-red-600 mb-0.5">Debit on this Line</p>
                    <p className="font-mono font-bold text-red-700">{billDrawerData.debit > 0 ? fmtC(billDrawerData.debit) : '—'}</p>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No bill data available</p>
            )}
          </div>
          <div className="px-6 py-3 border-t border-border flex items-center justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setBillDrawerOpen(false)} className="text-xs">
              Close
            </Button>
            {billDrawerData?.meta?.bill_id && (
              <Button size="sm" onClick={() => openBillFullPage(billDrawerData.meta.bill_id)} className="text-xs" data-testid="cd-open-bill-page">
                <ExternalLink className="h-3 w-3 mr-1" /> Open Bill Page
              </Button>
            )}
          </div>
        </SheetContent>
      </Sheet>
      </TooltipProvider>
    </MainLayout>
  );
}
